// INTEL CONFIDENTIAL
// Copyright (C) 2023 Intel Corporation

import React, { useState } from 'react'
import './SideNavAI.scss'
import { Button, Collapse, Nav, NavLink } from 'react-bootstrap'
import { BsChevronDown, BsChevronUp } from 'react-icons/bs'

interface NavItemChildren {
  url: string
  title: string
}

interface NavItem {
  url?: string
  title: string
  children?: NavItemChildren[]
  openInNewTab?: boolean
}

interface NavItemLinkProps {
  item: NavItem
  isDropdown?: boolean
  isSecondLevel?: boolean
  openMenu: string | null
  setOpenMenu: (value: string | null) => void
}

const NavItemLink: React.FC<NavItemLinkProps> = ({
  item,
  isDropdown,
  isSecondLevel,
  openMenu,
  setOpenMenu
}): JSX.Element => {
  const button = (
    <Nav.Item
      intc-id={`sidebarnavLink${item.url?.replaceAll(' ', '')}`}
      aria-label={`Go to ${item.title} Page`}
      className={'d-flex'}
      as={Nav.Link}
      onClick={() => {
        setOpenMenu(openMenu === item.title ? null : item.title)
      }}
      aria-expanded={openMenu === item.title}
    >
      <div className="d-flex wrapper">
        <span className={'w-100 d-show-animate'}>{item.title}</span>
      </div>
      <BsChevronDown className="ms-s2 caret-down" size={14} />
      <BsChevronUp className="ms-s2 caret-up" size={14} />
    </Nav.Item>
  )

  const link = (
    <Nav.Item
      intc-id={`link-nav-bar-collapsed-${item.title}`}
      data-wap_ref={`link-nav-bar-collapsed-${item.title}`}
      as={NavLink}
      href={item.url}
      role="link"
      target={item?.openInNewTab ? '_blank' : '_self'}
      className={`d-flex nav-link  ${isSecondLevel ? 'second-level' : ''} ${item.title === 'Contact' ? 'd-sm-none' : ''}`}
      rel={item?.openInNewTab ? 'noreferrer' : undefined}
    >
      <span className={'w-100 d-show-animate'}>{item.title}</span>
    </Nav.Item>
  )

  return isDropdown ? button : link
}

interface SideNavAIProps {
  showMenu?: boolean
  toggleMenuVisibility?: () => void
}

const SideNavAI: React.FC<SideNavAIProps> = ({ showMenu, toggleMenuVisibility }) => {
  const docsURL: any = process.env.DOCUMENTATION_URL
  const landingName: any = process.env.LANDING_LONG_NAME
  const contactSalesURL: any = process.env.CONTACT_SALES_URL
  const options = [
    { title: 'Home', url: '/' },
    { title: 'Products', url: '/products' },
    {
      title: 'Solutions',
      children: [
        { title: 'Enterprise', url: '/solutions/enterprise' },
        {
          title: 'Developers',
          url: '/solutions/developers'
        },
        {
          title: 'Startups',
          url: '/solutions/startups'
        }
      ]
    },
    { title: 'Support', url: '/support' },
    { title: 'Docs', url: docsURL, openInNewTab: true },
    { title: 'Contact', url: contactSalesURL, openInNewTab: false }
  ]
  const [openMenu, setOpenMenu] = useState<string | null>(null)

  const getItemChildren = (firstLevel: any): any[] => {
    const items: any[] = []
    if (firstLevel.children) {
      firstLevel.children.forEach((secondLevel: any, index: number) => {
        items.push(
          <NavItemLink item={secondLevel} key={index} isSecondLevel openMenu={openMenu} setOpenMenu={setOpenMenu} />
        )
      })
    }
    return items
  }

  const listItems: any = []
  options.forEach((firstLevel) => {
    if (firstLevel.children === undefined && firstLevel.url) {
      listItems.push(<NavItemLink item={firstLevel} openMenu={openMenu} setOpenMenu={setOpenMenu} />)
    }
    if (firstLevel.children && !firstLevel.url) {
      const menuId = firstLevel.title?.replaceAll(' ', '')
      const menu = (
        <>
          <NavItemLink item={firstLevel} openMenu={openMenu} setOpenMenu={setOpenMenu} isDropdown />
          <Collapse in={openMenu === firstLevel.title}>
            <ul className={'collapse submenu'} id={`subMenu${menuId}`}>
              {getItemChildren(firstLevel)}
            </ul>
          </Collapse>
        </>
      )
      listItems.push(menu)
    }
  })

  return (
    <>
      <div
        id="menu-overlay"
        className={showMenu ? 'show-overlay d-lg-none' : ''}
        onClick={toggleMenuVisibility}
        aria-hidden
      ></div>
      <div
        intc-id="site-navbar-navigation"
        role="navigation"
        className={`sideNavBar ${showMenu ? 'showSideNavBar' : 'd-none'} offcanvas-sm-width header-scroll-menu d-lg-none`}
      >
        <div className="d-flex flex-row gap-s6 p-s6 w-100 ">
          <div intc-id="site-navbar-navigation-label-expand" className="offcanvas-title h5">
            {landingName}
          </div>
          <Button
            variant="close"
            aria-label="Close"
            className="ms-auto align-self-end"
            onClick={toggleMenuVisibility}
          ></Button>
        </div>
        <div intc-id="SideBarNavigationBody" className="offcanvas-body py-s6">
          <Nav className="flex-column">
            {listItems.map((item: JSX.Element, index: number) => (
              <div key={index}>{item}</div>
            ))}
          </Nav>
        </div>
      </div>
    </>
  )
}

export default SideNavAI
